import * as React from "react";
import { format } from "date-fns";
import { Button, Card } from "@/components/ui";
import { cn } from "@/lib/utils";
import { PostStatsRow, sumPostStats } from "@/components/PostStatsRow";
import { formatRelativeTime } from "@/components/reviews/utils";
import { socialIcons, socialNetworkNames, SocialNetworksId } from "@/interfaces";
import { PostNetworkStatus, ReviewPostNetwork, ReviewPostSummary } from "@/services/bedrock/dtos/ReviewPost";
import { MiniPager } from "./MiniPager";

export interface SharedOnSocialSectionProps {
  reviewId: number;
  posts: ReviewPostSummary[];
  mode?: "player" | "coach";
  playerName?: string;
  onShareAgain: () => void;
  onOpenPost: (postId: number, socialNetworkId: SocialNetworksId | null) => void;
  /** False once the review can't be posted anymore (consent withdrawn, deactivated). Default true. */
  canShareAgain?: boolean;
}

/** One line for where a post stands: scheduled, posting, posted, failed, or removed. */
export function statusLine(post: ReviewPostSummary): string {
  switch (post.status) {
    case "scheduled":
      // formatRelativeTime only counts backwards, so a future date is written out.
      return `Scheduled for ${format(new Date(post.scheduledAt ?? post.createdAt), "MMM d, h:mm a")}`;
    case "posting":
      return "Posting…";
    case "posted":
      return `Posted ${formatRelativeTime(post.postedAt ?? post.createdAt)}`;
    case "failed":
      return "Couldn't post. Open to retry.";
    case "deleted":
      return "Removed from social";
  }
}

/** How a network that didn't follow its post's rollup ended up, as the end of a sentence. */
const NETWORK_EXCEPTIONS: Partial<Record<PostNetworkStatus, string>> = {
  POSTED: "posted",
  IN_TRANSIT: "is still posting",
  FAILED: "couldn't post",
  SKIPPED_NO_CONNECTION: "wasn't connected",
};

/** The network statuses each post-level rollup already says, so naming them again would repeat it. */
const ROLLUP_NETWORK_STATUSES: Record<ReviewPostSummary["status"], PostNetworkStatus[]> = {
  scheduled: ["PENDING"],
  posting: ["PENDING", "IN_TRANSIT"],
  posted: ["POSTED"],
  failed: ["FAILED", "FAILURE_ACKED"],
  deleted: ["DELETED"],
};

export const networkName = (network: ReviewPostNetwork): string =>
  socialNetworkNames[network.code as SocialNetworksId] ?? network.code;

/** "Instagram couldn't post": only the networks whose outcome the status line doesn't already say. */
export function networkExceptions(post: ReviewPostSummary): string[] {
  return post.networks.flatMap(network => {
    if (ROLLUP_NETWORK_STATUSES[post.status].includes(network.status)) return [];
    const outcome = NETWORK_EXCEPTIONS[network.status];
    return outcome ? [`${networkName(network)} ${outcome}`] : [];
  });
}

/** The network's logo image. The PNGs come out more even in size than the masked SVGs. */
const NetworkImage = ({ code }: { code: string }): JSX.Element => (
  <img src={socialIcons[code as SocialNetworksId]} alt="" className="size-6 object-contain" />
);

/** The post's networks; each one that's live links to it. */
export function NetworkLinks({ networks }: { networks: ReviewPostNetwork[] }): JSX.Element {
  return (
    <span className="flex items-center gap-1.5" data-testid="network-links">
      {networks.map(network => {
        const name = networkName(network);
        return network.externalPostUrl ? (
          <a
            key={network.code}
            href={network.externalPostUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex rounded hover:opacity-75"
            aria-label={`View on ${name}`}
            title={`View on ${name}`}
          >
            <NetworkImage code={network.code} />
          </a>
        ) : (
          <span key={network.code} className="flex opacity-50" title={name} aria-label={name} role="img">
            <NetworkImage code={network.code} />
          </span>
        );
      })}
    </span>
  );
}

/** Network links, then the post's combined counts, or a note that they're on their way. */
export function PostReach({ post, className }: { post: ReviewPostSummary; className?: string }): JSX.Element {
  const counts = sumPostStats(post.networks.map(network => network.stats));
  return (
    <div className={cn("flex flex-wrap items-center gap-x-3 gap-y-1", className)}>
      <NetworkLinks networks={post.networks} />
      {counts ? (
        <PostStatsRow counts={counts} />
      ) : (
        post.status === "posted" && <span className="text-xs text-slate-400">Stats usually arrive within a day</span>
      )}
    </div>
  );
}

export const SharedOnSocialSection: React.FC<SharedOnSocialSectionProps> = ({
  posts,
  mode = "player",
  canShareAgain = true,
  onShareAgain,
  onOpenPost,
}) => {
  const [index, setIndex] = React.useState(0);
  const current = posts[Math.min(index, posts.length - 1)];
  if (!current) return null;

  const openCurrent = () =>
    onOpenPost(current.postId, (current.primarySocialNetworkId as SocialNetworksId | null) ?? null);
  const exceptions = networkExceptions(current);
  const footer = [
    ...exceptions,
    ...(mode === "coach" && current.createdBy ? [`Posted by ${current.createdBy.name}`] : []),
  ];

  // The page's card language (padding, shadow, a 48px tile beside a bold title), with the post's
  // thumbnail in the tile.
  return (
    <Card
      className="flex flex-col p-6 md:p-8 border border-slate-200 bg-white rounded-2xl shadow-sm"
      data-testid="shared-on-social"
    >
      <div className="flex items-start gap-4">
        <button type="button" onClick={openCurrent} className="shrink-0 cursor-pointer" aria-label="Open post">
          {current.thumbnailUrl ? (
            <img
              src={current.thumbnailUrl}
              alt=""
              className="size-12 rounded-2xl border border-slate-200 object-cover shadow-inner"
            />
          ) : (
            <div className="size-12 rounded-2xl border border-slate-200 bg-slate-100 shadow-inner" />
          )}
        </button>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            <div className="font-bold text-lg text-slate-900 tracking-tight">Shared on social</div>
            {posts.length > 1 && <MiniPager index={index} total={posts.length} onChange={setIndex} />}
          </div>
          <button
            type="button"
            onClick={openCurrent}
            className="text-left text-sm font-medium text-slate-500 hover:underline cursor-pointer"
          >
            {statusLine(current)}
          </button>
          <PostReach post={current} className="mt-2" />
          {(footer.length > 0 || canShareAgain) && (
            <div className="flex flex-wrap items-center gap-x-1 text-xs text-slate-500">
              {footer.join(" · ")}
              {canShareAgain && (
                <>
                  {footer.length > 0 && <span aria-hidden="true">·</span>}
                  <Button variant="link" size="inline" className="text-xs text-slate-500" onClick={onShareAgain}>
                    Share again
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
